import { Panel, ToolButton } from '@rcs/ui';
import { useEffect, type ReactNode } from 'react';
import { isCleanToggleKey } from '../camera/shortcuts';
import { messages } from '../i18n/ja';
import { useMovableWindow, useWindowLayout } from '../layout/movable';
import { FLOATING_IDS, OVERLAY_IDS, type FloatingId } from '../layout/window-layout';

/**
 * 画面の端のパネル（コース・制御点・プロパティ・解析）。全画面のときは、3D の上に浮かぶウィンドウになり、
 * 動かす・閉じることができる（出すかどうかは {@link FullscreenMenu} で選ぶ）。
 */
export function FloatingPanel({
  id,
  title,
  className,
  floating,
  children,
}: {
  readonly id: Exclude<FloatingId, 'toolbar'>;
  readonly title: string;
  readonly className: string;
  /** 全画面で、浮かぶウィンドウにするか */
  readonly floating: boolean;
  readonly children: ReactNode;
}) {
  const layout = useWindowLayout();
  const { attach, style, handle } = useMovableWindow<HTMLElement>(id, title);
  if (!floating) {
    return (
      <Panel title={title} className={className}>
        {children}
      </Panel>
    );
  }
  const shown = layout?.isShown(id) ?? false;
  return (
    <Panel
      ref={attach}
      title={title}
      className={[className, 'floating-window', shown ? '' : 'floating-window--hidden']
        .filter(Boolean)
        .join(' ')}
      style={style}
      data-testid={`window-${id}`}
      actions={
        <>
          {handle}
          <CloseButton id={id} name={title} />
        </>
      }
    >
      {children}
    </Panel>
  );
}

/** ツールバー。全画面のときは、浮かぶウィンドウになる。 */
export function FloatingToolbar({
  floating,
  children,
}: {
  readonly floating: boolean;
  readonly children: ReactNode;
}) {
  const layout = useWindowLayout();
  const name = messages.windows.names.toolbar;
  const { attach, style, handle } = useMovableWindow<HTMLElement>('toolbar', name);
  if (!floating) return <header className="toolbar">{children}</header>;
  const shown = layout?.isShown('toolbar') ?? false;
  return (
    <header
      ref={attach}
      className={['toolbar', 'floating-window', shown ? '' : 'floating-window--hidden']
        .filter(Boolean)
        .join(' ')}
      style={style}
      data-testid="window-toolbar"
    >
      {handle}
      {children}
      <CloseButton id="toolbar" name={name} />
    </header>
  );
}

function CloseButton({ id, name }: { readonly id: FloatingId; readonly name: string }) {
  const layout = useWindowLayout();
  return (
    <ToolButton
      label={messages.windows.close(name)}
      icon={<span aria-hidden="true">✕</span>}
      iconOnly
      onClick={() => {
        layout?.setShown(id, false);
        // 閉じたウィンドウの中にあったフォーカスを、全画面の操作の切り替えのボタンへ移す
        document.getElementById(showButtonId(id))?.focus();
      }}
      data-testid={`close-${id}`}
    />
  );
}

/**
 * 全画面のときの操作：全画面の終了・3D の上の表示（HUD・視点・再生の操作など）と、ツールバー・パネルの
 * 出し入れ・「すっきり」（すべて隠す）・配置を戻す。3D ビューの上端の中央に置く。
 * たたむと、小さなボタン 1 つになる（画面を広く使える）。H キーでも「すっきり」を切り替えられる。
 */
export function FullscreenMenu({ onExit }: { readonly onExit: () => void }) {
  const layout = useWindowLayout();
  const t = messages.windows;
  const { attach, element, style, handle } = useMovableWindow<HTMLDivElement>('menu', t.names.menu);
  // 高さを親要素の CSS 変数 `--rcs-menu-height` に出し、HUD・視点・浮かぶウィンドウを、その下に置く
  useEffect(() => {
    const parent = element?.parentElement;
    if (!element || !parent || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      parent.style.setProperty('--rcs-menu-height', `${String(element.offsetHeight)}px`);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      parent.style.removeProperty('--rcs-menu-height');
    };
  }, [element]);
  const collapsed = layout?.collapsed ?? false;
  const clean = layout?.clean ?? false;
  return (
    <div
      ref={attach}
      className={collapsed ? 'fullscreen-menu fullscreen-menu--collapsed' : 'fullscreen-menu'}
      style={style}
      role="toolbar"
      aria-label={t.menu}
      data-testid="fullscreen-menu"
      data-collapsed={collapsed}
    >
      {handle}
      {collapsed ? (
        <ToolButton
          label={clean ? t.restoreAll : t.unfold}
          title={clean ? t.restoreAllHint : t.unfoldHint}
          aria-keyshortcuts="H"
          onClick={() => {
            if (clean) layout?.toggleClean();
            else layout?.setCollapsed(false);
          }}
          data-testid="fullscreen-unfold"
        />
      ) : (
        <>
          <ToolButton
            label={messages.toolbar.exitFullscreen}
            onClick={onExit}
            data-testid="fullscreen-exit"
          />
          <ToolButton
            label={t.hideAll}
            title={t.hideAllHint}
            aria-keyshortcuts="H"
            onClick={() => {
              layout?.toggleClean();
            }}
            data-testid="fullscreen-clean"
          />
          <div className="fullscreen-menu__group" role="group" aria-label={t.overlays}>
            {OVERLAY_IDS.map((id) => (
              <ToolButton
                key={id}
                label={t.names[id]}
                pressed={layout?.isOverlayShown(id) ?? true}
                onClick={() => {
                  layout?.setOverlay(id, !layout.isOverlayShown(id));
                }}
                data-testid={`overlay-${id}`}
              />
            ))}
          </div>
          <div className="fullscreen-menu__group" role="group" aria-label={t.show}>
            {FLOATING_IDS.map((id) => (
              <ToolButton
                key={id}
                id={showButtonId(id)}
                label={t.names[id]}
                pressed={layout?.isShown(id) ?? false}
                onClick={() => {
                  layout?.setShown(id, !layout.isShown(id));
                }}
                data-testid={`show-${id}`}
              />
            ))}
          </div>
          <LayoutResetButton />
          <ToolButton
            label={t.fold}
            title={t.foldHint}
            onClick={() => {
              layout?.setCollapsed(true);
            }}
            data-testid="fullscreen-fold"
          />
        </>
      )}
    </div>
  );
}

/** いまの画面の状態（通常・全画面）で動かしたウィンドウを、元の位置に戻す。 */
export function LayoutResetButton() {
  const layout = useWindowLayout();
  const t = messages.windows;
  return (
    <ToolButton
      label={t.reset}
      title={t.resetHint}
      disabled={!(layout?.moved ?? false)}
      onClick={() => {
        layout?.reset();
      }}
      data-testid="layout-reset"
    />
  );
}

/** 全画面の操作の、ウィンドウを出す切り替えのボタンの要素 ID。 */
function showButtonId(id: FloatingId): string {
  return `rcs-show-window-${id}`;
}

/** 全画面のとき、H キーで「すっきり」（すべて隠す）と、表示を戻すを切り替える。 */
export function FullscreenKeys() {
  const layout = useWindowLayout();
  const toggle = layout?.toggleClean;
  useEffect(() => {
    if (!toggle) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (!isCleanToggleKey(event)) return;
      event.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [toggle]);
  return null;
}
